/**
 * Settings response → the form model the wizard edits.
 *
 * Extracted from OnboardingPage in #2493 so the reminder can run the same
 * readiness rules against the same shape. Two copies of this mapping would be
 * two chances for the reminder and the wizard to disagree about whether a
 * field is filled.
 */

import type { WorkshopSettings } from '@/types';
import { normalizeOpeningHours } from './commitHoursStep';

/** Shape the settings response into the form model the step edits. */
export function toWorkshopSettings(name: string, s: Record<string, unknown>): WorkshopSettings {
  const str = (k: string) => (typeof s[k] === 'string' ? (s[k] as string) : '');
  const num = (k: string) => (typeof s[k] === 'number' ? (s[k] as number) : undefined);

  return {
    name,
    address: str('address'),
    city: str('city'),
    postalCode: str('postalCode'),
    country: str('country') || 'Finland',
    phone: str('contactPhone'),
    email: str('contactEmail'),
    businessId: str('businessId'),
    vatNumber: str('vatNumber'),
    bankIban: str('bankIban'),
    bankBic: str('bankBic'),
    timezone: str('timezone') || 'Europe/Helsinki',
    currency: str('currency') || 'EUR',
    laborRateCents: num('laborRateCents') ?? 0,
    laborRateGrossCents: num('laborRateGrossCents'),
    // The wizard does not edit VAT rates — provisioning seeds the Finnish ones.
    // They are carried through untouched so the settings PATCH cannot blank them.
    vatRates: Array.isArray(s.vatRates) ? (s.vatRates as WorkshopSettings['vatRates']) : [],
    defaultVatRateId: typeof s.defaultVatRateId === 'string' ? s.defaultVatRateId : 'fi-25.5',
    schedulingLookaheadDays: num('schedulingLookaheadDays') ?? 4,
    defaultSchedulingType: s.defaultSchedulingType === 'exact' ? 'exact' : 'floating',
    defaultQuoteValidityDays: num('defaultQuoteValidityDays') ?? 14,
    workdayUnpaidLunchMinutes: num('workdayUnpaidLunchMinutes') ?? 30,
    openingHours: normalizeOpeningHours(s.businessHours),
  };
}
